import { Injectable } from '@angular/core';
import {
  Firestore,
  doc,
  docData,
  collection,
  collectionData,
  query,
  where,
  orderBy,
  DocumentReference,
  getDoc,
  DocumentSnapshot,
  runTransaction,
  CollectionReference, onSnapshot, docSnapshots
} from '@angular/fire/firestore';
import {catchError, from, Observable, of, switchMap} from 'rxjs';
import { FSMenu, FSMenuAddendum, MenuAddendum, MenuStructure } from '../models/FSMenu';
import {map, tap} from 'rxjs/operators';
import { FSConfig, FSConnectAccount, VenueConfig } from '../models/FSConfig';
import { AccountEvent } from '../models/AccountEvent';
import { FSPayment } from '../models/PaymentModels';
import {Translation} from "../models/Translation";
import {PaymentSession} from '../models/payment-session';
import {FSVoucher} from '../models/FSVoucher';

@Injectable({
  providedIn: 'root'
})
export class FireService {

  constructor(private firestore: Firestore) { }

  observeMenuStructure(venueId: string): Observable<MenuStructure> {
    const docRef = doc(this.firestore, `menus/${venueId}:menu`);
    return new Observable<MenuStructure>(sub =>
      onSnapshot(docRef, snap => {
        const data = snap.data() as FSMenu;
        sub.next(data ? JSON.parse(data.data) : null);
      })
    );
  }

  observeMenuAddendum(venueId: string): Observable<MenuAddendum> {
    const docRef = doc(this.firestore, `addendums/${venueId}`);
    return new Observable<MenuAddendum>(sub =>
      onSnapshot(docRef, snap => {
        const data = snap.data() as FSMenuAddendum;
        sub.next(data ? JSON.parse(data.data) : null);
      })
    );
  }

  observeTranslations(venueId: number, lang: string): Observable<Translation[]> {
    console.log("observeTranslations", venueId, lang);
    return new Observable<Translation[]>(observer => {
      const translationsRef = collection(this.firestore, 'translations');
      const q = query(
        translationsRef,
        where("venue_id", "==", venueId),
        where("lang", "==", lang)
      );

      return onSnapshot(q,
        (querySnapshot) => {
          const translations = querySnapshot.docs.map(doc => doc.data() as Translation);
          observer.next(translations);
        },
        (error) => {
          observer.error(error);
        });
    });
  }

  getTranslation(venueId: number, lang: string, strId: string): Observable<Translation | null> {
    const docRef = doc(this.firestore, `translations/${lang}:${strId}:${venueId}`);
    return from(getDoc(docRef)).pipe(
      map(snapshot => snapshot.data() as Translation | null)
    );
  }

  observeTranslation(venueId: number, lang: string, strId: string): Observable<Translation> {
    const docRef = doc(this.firestore, `translations/${lang}:${strId}:${venueId}`);
    return new Observable<Translation>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data() as Translation);
      })
    );
  }

  // observeTranslation(venueId: number, lang: string, strId: string): Observable<Translation | null> {
  //   const docRef = doc(this.firestore, `translations/${lang}:${strId}:${venueId}`);
  //   return docData<Translation>(docRef);
  // }

  observeVenueConfig(venueId: string): Observable<VenueConfig> {
    const docRef = doc(this.firestore, `configs/${venueId}:cfg`);
    return new Observable<VenueConfig>(sub =>
      onSnapshot(docRef, snap => {
        const data = snap.data() as FSConfig;
        sub.next(data ? JSON.parse(data.data) : null);
      })
    );
  }

  observeEvents(chatId: number): Observable<AccountEvent[]> {
    return new Observable<AccountEvent[]>(observer => {
      const eventsRef = collection(this.firestore, 'account_events');
      const q = query(
        eventsRef,
        where("chat_id", "==", chatId),
        orderBy("date", "asc")
      );

      return onSnapshot(q,
        (querySnapshot) => {
          const events = querySnapshot.docs.map(doc => doc.data() as AccountEvent);
          observer.next(events);
        },
        (error) => {
          observer.error(error);
        });
    });
  }

  observeEventsWindow(chatId: number, newerThan: Date): Observable<AccountEvent[]> {
    return new Observable<AccountEvent[]>(observer => {
      const eventsRef = collection(this.firestore, 'account_events');
      const q = query(
        eventsRef,
        where("chat_id", "==", chatId),
        where("date", ">", newerThan),
        orderBy("date", "asc")
      );

      return onSnapshot(q,
        (querySnapshot) => {
          const events = querySnapshot.docs.map(doc => doc.data() as AccountEvent);
          observer.next(events);
        },
        (error) => {
          observer.error(error);
        });
    });
  }

  observePayment(receiptKey: string): Observable<FSPayment | null> {
    const docRef = doc(this.firestore, `payments/${receiptKey}`);
    return new Observable<FSPayment | null>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data() as FSPayment | null);
      })
    );
  }

  observeLivesplit(table: string, venueId: number): Observable<any> {
    const docRef = doc(this.firestore, `livesplit/${table}:${venueId}`);
    return new Observable<any>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data());
      })
    );
  }

  observePaymentSession(table: string, venueId: number): Observable<PaymentSession | null> {
    const docRef = doc(this.firestore, `payment_sessions/${venueId}_${table}`);
    return new Observable<any>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data() as PaymentSession | null);
      })
    );
  }

  observeVouchersForChat(chatId: number): Observable<FSVoucher[] | null> {
    return new Observable<FSVoucher[]>(observer => {
      const ref = collection(this.firestore, 'vouchers');
      const q = query(
        ref,
        where("chat_id", "==", chatId),
      );

      return onSnapshot(q,
        (querySnapshot) => {
          const vouchers = querySnapshot.docs.map(doc => doc.data() as FSVoucher);
          observer.next(vouchers);
        },
        (error) => {
          observer.error(error);
        });
    });
  }

  getLivesplitDocRef(table: string, venueId: number): DocumentReference {
    return doc(this.firestore, `livesplit/${table}:${venueId}`);
  }

  runTransaction<T>(updateFunction: (transaction: any) => Promise<T>): Promise<T> {
    return runTransaction(this.firestore, updateFunction);
  }

  observeConnectAccount(documentId: string): Observable<FSConnectAccount | null> {
    console.log(`Observing: connect_accounts/${documentId}`);
    const docRef = doc(this.firestore, `connect_accounts/${documentId}`);
    return new Observable<FSConnectAccount | null>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data() as FSConnectAccount | null);
      })
    );
  }

  observeMiniSplit(table: string, venueId: number): Observable<any> {
    const docRef = doc(this.firestore, `minisplit/${table}:${venueId}`);
    return new Observable<any>(sub =>
      onSnapshot(docRef, snap => {
        sub.next(snap.data());
      })
    );
  }
}
